import { useState, useCallback, useEffect } from 'react';
import {
  payAmigoClient,
  payAmigoWebSocket,
  ExecuteResponse,
  PaymentStatus,
  StatusUpdateResponse,
} from '../api/payamigo';

interface UsePayAmigoPaymentResult {
  executeResult: ExecuteResponse | null;
  currentStatus: PaymentStatus | null;
  isExecuting: boolean;
  error: string | null;
  executePayment: (quoteId: string) => Promise<void>;
  clearPayment: () => void;
}

export const usePayAmigoPayment = (): UsePayAmigoPaymentResult => {
  const [executeResult, setExecuteResult] = useState<ExecuteResponse | null>(null);
  const [currentStatus, setCurrentStatus] = useState<PaymentStatus | null>(null);
  const [isExecuting, setIsExecuting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const clearPayment = useCallback(() => {
    setExecuteResult(null);
    setCurrentStatus(null);
    setError(null);
    payAmigoWebSocket.unsubscribeFromTransaction();
  }, []);

  const executePayment = useCallback(async (quoteId: string) => {
    setIsExecuting(true);
    setError(null);

    try {
      const response = await payAmigoClient.executePayment({ quote_id: quoteId });
      setExecuteResult(response);
      setCurrentStatus(response.status);

      // Connect to WebSocket and subscribe to transaction updates
      payAmigoWebSocket.connect();
      
      // Unsubscribe from any previous transaction before subscribing to new one
      payAmigoWebSocket.unsubscribeFromTransaction();
      
      payAmigoWebSocket.subscribeToTransaction(
        response.transaction_id,
        (confirmedData) => {
          console.log('[payments] ✅ Subscription confirmed:', confirmedData);
        },
        (statusUpdate: StatusUpdateResponse) => {
          console.log('[payments] 📊 Status update received:', statusUpdate);
          console.log('[payments] 📊 New status:', statusUpdate.status);
          console.log('[payments] 📊 Transaction ID:', statusUpdate.transactionId);
          setCurrentStatus(statusUpdate.status);
        },
        (errorData) => {
          console.error('[payments] ❌ Subscription error:', errorData);
          setError('WebSocket subscription error');
        }
      );

      console.log('[payments] 🔌 Subscribed to transaction:', response.transaction_id);
      console.log('[payments] 🔌 Initial status:', response.status);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to execute payment';
      setError(errorMessage);
      console.error('Payment execution error:', err);
    } finally {
      setIsExecuting(false);
    }
  }, []);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      payAmigoWebSocket.unsubscribeFromTransaction();
      payAmigoWebSocket.disconnect();
    };
  }, []);

  return {
    executeResult,
    currentStatus,
    isExecuting,
    error,
    executePayment,
    clearPayment,
  };
};

